import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  Stack,
  SxProps,
  TextFieldProps,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import moment, { Moment } from 'moment';

import { useAppSelector } from '../../../../../hooks';
import { checkUserPermission } from '../../../../../helper';
import { getAuthState } from '../../../../../store/AuthSlice';
import {
  ADMIN_RESPOND_ZOOM_MEETING_REQUEST_RESPONSE_TYPES,
  adminGetZoomMeetingRequestById,
  adminRespondZoomMeetingRequest,
  adminUpdateZoomMeetingRequest,
  getZoomRequestsState,
  resetAdminZoomRequestData,
  TAdminRespondZoomMeetingRequest,
  ZOOM_MEETING_REQUEST_STATUSES,
  ZOOM_MEETING_REQUEST_TYPES,
} from '../../../../../store/V2/ZoomRequestsSlice';
import BasicLoader from '../../../../../components/Loaders/BasicLoader';
import ShortUserCard from '../../../../../components/V2/Cards/ShortUserCard';
import { TPTextField } from '../../../../../components/Inputs';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../store/V2/RolesSlice';

interface ViewZoomRequestModalProps {
  open: boolean;
  onClose: () => void;
  requestId: string;
}

type TState = {
  penalty: string;
  penaltyReason: string;
  adminNote: string;
};

const actionButtonStyles: SxProps = {
  padding: { xs: '6px 12px', sm: '12px 24px' },
  borderRadius: '6px',
  fontWeight: 600,
  fontSize: '12px',
};

const CustomDivider = <div style={{ borderBottom: '1px dashed #94A3B8' }} />;

const PenaltyAlertCard = ({ message }: { message: string }) => {
  return (
    <div
      style={{
        backgroundColor: '#FF5630',
        borderRadius: '4px',
        padding: '10px',
      }}
    >
      <Typography color="#ffffff" fontWeight={500} fontSize="12px">
        {message}
      </Typography>
    </div>
  );
};

const renderWithinLayout = (
  grid1: React.ReactElement,
  grid2: React.ReactElement
) => {
  return (
    <Grid container alignItems="center" columnGap="10px">
      <Grid item>{grid1}</Grid>
      <Grid item xs>
        {grid2}
      </Grid>
    </Grid>
  );
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '40px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  penalty: '',
  penaltyReason: '',
  adminNote: '',
};

const ViewZoomRequestModal: FunctionComponent<ViewZoomRequestModalProps> = ({
  open,
  onClose,
  requestId,
}) => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const zoomRequestsState = useAppSelector(getZoomRequestsState);

  // State
  const [state, setState] = useState({ ...initialState });
  const [isApproveLoading, setApproveLoading] = useState(false);
  const [isRejectLoading, setRejectLoading] = useState(false);
  const [isUpdateLoading, setUpdateLoading] = useState(false);

  const authUser = authState.user;
  const zoomRequestData = zoomRequestsState.adminZoomRequestData;

  let isApprovable = false;
  let isRejectable = false;
  let isUpdatable = false;
  let startDate: Moment | undefined;
  let newStartDate: Moment | undefined;
  let reschedulebadgesVisibility = false;
  let requestedByRoleAndName = '';

  if (zoomRequestData) {
    if (zoomRequestData.start) {
      startDate = moment(zoomRequestData.start);
    }

    if (zoomRequestData.newStart) {
      newStartDate = moment(zoomRequestData.newStart);
    }

    if (
      zoomRequestData.start &&
      zoomRequestData.newStart &&
      zoomRequestData.type === ZOOM_MEETING_REQUEST_TYPES.RESCHEDULE
    ) {
      reschedulebadgesVisibility = true;
    }

    if (zoomRequestData.status !== ZOOM_MEETING_REQUEST_STATUSES.PENDING) {
      isUpdatable = true;
    }

    if (zoomRequestData.requestedByRole) {
      requestedByRoleAndName = zoomRequestData.requestedByRole.toLocaleLowerCase();

      if (
        zoomRequestData.requestedBy &&
        zoomRequestData.requestedBy &&
        zoomRequestData.requestedBy.firstname
      ) {
        requestedByRoleAndName += ` (${zoomRequestData.requestedBy.firstname}${
          zoomRequestData.requestedBy.lastname
            ? ` ${zoomRequestData.requestedBy.lastname}`
            : ''
        })`;
      }
    }
  }

  if (
    zoomRequestData &&
    zoomRequestData.start &&
    moment(zoomRequestData.start) > moment() &&
    zoomRequestData.status === ZOOM_MEETING_REQUEST_STATUSES.PENDING &&
    checkUserPermission(authUser, {
      adminSchedule: [USER_PERMISSION_INDIVIDUALS.SCHEDULE_EDIT],
    })
  ) {
    if (
      zoomRequestData.type === ZOOM_MEETING_REQUEST_TYPES.RESCHEDULE &&
      zoomRequestData.newStart &&
      moment(zoomRequestData.newStart) > moment()
    ) {
      isApprovable = true;
      isRejectable = true;
    } else if (zoomRequestData.type === ZOOM_MEETING_REQUEST_TYPES.CANCEL) {
      isApprovable = true;
      isRejectable = true;
    }
  }

  const isEditable = isApprovable || isRejectable || isUpdatable;

  useEffect(() => {
    dispatch(
      adminGetZoomMeetingRequestById({
        id: requestId,
      })
    );

    return () => {
      dispatch(resetAdminZoomRequestData());
    };
  }, []);

  useEffect(() => {
    if (zoomRequestData) {
      setState((prevState) => {
        const newState = { ...prevState };

        if (zoomRequestData.penalty) {
          newState.penalty = zoomRequestData.penalty.toString();
        }

        if (zoomRequestData.penaltyReason) {
          newState.penaltyReason = zoomRequestData.penaltyReason;
        }

        if (zoomRequestData.adminNote) {
          newState.adminNote = zoomRequestData.adminNote;
        }

        return newState;
      });
    }
  }, [zoomRequestData]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({ ...prevState, ...attributes }));
  };

  const handleApproveRequest = async () => {
    setApproveLoading(true);

    const apiData: TAdminRespondZoomMeetingRequest = {
      requestId,
      response: ADMIN_RESPOND_ZOOM_MEETING_REQUEST_RESPONSE_TYPES.APPROVE,
    };

    if (state.penalty) {
      apiData.penalty = parseFloat(state.penalty);

      if (state.penaltyReason.trim().length) {
        apiData.penaltyReason = state.penaltyReason.trim();
      }
    }

    if (state.adminNote.trim().length) {
      apiData.adminNote = state.adminNote.trim();
    }

    await dispatch(adminRespondZoomMeetingRequest(apiData));

    setApproveLoading(false);
  };

  const handleRejectRequest = async () => {
    setRejectLoading(true);

    const apiData: TAdminRespondZoomMeetingRequest = {
      requestId,
      response: ADMIN_RESPOND_ZOOM_MEETING_REQUEST_RESPONSE_TYPES.REJECT,
    };

    if (state.penalty) {
      apiData.penalty = parseFloat(state.penalty);

      if (state.penaltyReason.trim().length) {
        apiData.penaltyReason = state.penaltyReason.trim();
      }
    }

    if (state.adminNote.trim().length) {
      apiData.adminNote = state.adminNote.trim();
    }

    await dispatch(adminRespondZoomMeetingRequest(apiData));

    setRejectLoading(false);
  };

  const handleUpdateRequest = async () => {
    setUpdateLoading(true);

    const apiData: TAdminRespondZoomMeetingRequest = {
      requestId,
      response: ADMIN_RESPOND_ZOOM_MEETING_REQUEST_RESPONSE_TYPES.UPDATE,
    };

    if (state.penalty) {
      apiData.penalty = parseFloat(state.penalty);

      if (state.penaltyReason.trim().length) {
        apiData.penaltyReason = state.penaltyReason.trim();
      }
    }

    if (state.adminNote.trim().length) {
      apiData.adminNote = state.adminNote.trim();
    }

    await dispatch(adminUpdateZoomMeetingRequest(apiData));

    setUpdateLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', maxWidth: '500px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Request Details
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent sx={{ p: { xs: '0 15px', sm: '0 30px' } }}>
        {zoomRequestsState.status === 'loading' && !zoomRequestData ? (
          <Stack sx={{ p: '15px 0 50px 0' }}>
            <BasicLoader />
          </Stack>
        ) : null}
        {zoomRequestData ? (
          <Stack rowGap="20px">
            {zoomRequestData.tutor ? (
              <div>
                {renderWithinLayout(
                  <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                    Tutor :
                  </Typography>,
                  <ShortUserCard
                    short
                    firstName={zoomRequestData.tutor.firstname}
                    lastName={zoomRequestData.tutor.lastname}
                    avatar={zoomRequestData.tutor.avatar || ''}
                  />
                )}
              </div>
            ) : null}
            {zoomRequestData.student ? (
              <div>
                {renderWithinLayout(
                  <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                    Student :
                  </Typography>,
                  <ShortUserCard
                    short
                    firstName={zoomRequestData.student.firstname}
                    lastName={zoomRequestData.student.lastname}
                    avatar={zoomRequestData.student.avatar || ''}
                  />
                )}
              </div>
            ) : null}
            <div>
              {renderWithinLayout(
                <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                  Requested by :
                </Typography>,
                <Typography
                  fontSize="16px"
                  fontWeight={500}
                  color="#94A3B8"
                  textTransform="capitalize"
                >
                  {requestedByRoleAndName}
                </Typography>
              )}
            </div>

            {CustomDivider}

            {startDate ? (
              <div>
                {renderWithinLayout(
                  <EventAvailableIcon
                    sx={{ width: '24px', height: '24px', color: '#000000' }}
                  />,
                  <Stack direction="row" rowGap="10px" columnGap="10px">
                    <Typography
                      fontSize="16px"
                      fontWeight={500}
                      color="#1E293B"
                    >
                      {startDate.format('dddd MMM DD YYYY hh:mm A')}
                    </Typography>
                    {reschedulebadgesVisibility ? (
                      <Chip
                        size="small"
                        label="Old"
                        sx={{
                          fontWeight: 500,
                          fontSize: '10px',
                          backgroundColor: '#94A3B8',
                          color: '#1E293B',
                          borderRadius: '3px',
                          p: '3px 6px',
                        }}
                      />
                    ) : null}
                  </Stack>
                )}
              </div>
            ) : null}
            {newStartDate ? (
              <div>
                {renderWithinLayout(
                  <EventAvailableIcon
                    sx={{ width: '24px', height: '24px', color: '#000000' }}
                  />,
                  <Stack direction="row" rowGap="10px" columnGap="10px">
                    <Typography
                      fontSize="16px"
                      fontWeight={500}
                      color="#1E293B"
                      sx={{ textDecoration: 'underline' }}
                    >
                      {newStartDate.format('dddd MMM DD YYYY hh:mm A')}
                    </Typography>
                    {reschedulebadgesVisibility ? (
                      <Chip
                        size="small"
                        label="New"
                        sx={{
                          fontWeight: 500,
                          fontSize: '10px',
                          backgroundColor: '#3498DB',
                          color: '#F8FAFC',
                          borderRadius: '3px',
                          p: '3px 6px',
                        }}
                      />
                    ) : null}
                  </Stack>
                )}
              </div>
            ) : null}
            <div>
              {renderWithinLayout(
                <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                  Reason :
                </Typography>,
                <Typography
                  fontSize="16px"
                  fontWeight={500}
                  color="#94A3B8"
                  textTransform="capitalize"
                >
                  {zoomRequestData.reason || '-'}
                </Typography>
              )}
            </div>

            {CustomDivider}

            {zoomRequestData.penaltyMessage &&
            zoomRequestData.status === ZOOM_MEETING_REQUEST_STATUSES.PENDING ? (
              <PenaltyAlertCard message={zoomRequestData.penaltyMessage} />
            ) : null}

            <div>
              {renderWithinLayout(
                <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                  Penalty :
                </Typography>,
                <FormControl fullWidth>
                  <TPTextField
                    {...textFieldProps}
                    type="number"
                    disabled={!isEditable}
                    placeholder="Enter here..."
                    value={state.penalty}
                    onChange={(e) =>
                      updateStateAttributes({ penalty: e.target.value })
                    }
                  />
                </FormControl>
              )}
            </div>
            <div>
              {renderWithinLayout(
                <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                  Reason :
                </Typography>,
                <FormControl fullWidth>
                  <TPTextField
                    {...textFieldProps}
                    disabled={!isEditable}
                    placeholder="Enter here..."
                    value={state.penaltyReason}
                    onChange={(e) =>
                      updateStateAttributes({ penaltyReason: e.target.value })
                    }
                  />
                </FormControl>
              )}
            </div>

            {CustomDivider}

            <div>
              {renderWithinLayout(
                <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                  Admin Note :
                </Typography>,
                <FormControl fullWidth>
                  <TPTextField
                    multiline
                    rows={2}
                    disabled={!isEditable}
                    placeholder="Enter here..."
                    value={state.adminNote}
                    onChange={(e) =>
                      updateStateAttributes({ adminNote: e.target.value })
                    }
                  />
                </FormControl>
              )}
            </div>
          </Stack>
        ) : null}
      </DialogContent>
      {isApprovable || isRejectable || isUpdatable ? (
        <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
          <Stack direction="row" alignItems="center" columnGap="10px">
            {isRejectable ? (
              <LoadingButton
                disableElevation
                variant="contained"
                color="inherit"
                disabled={isApproveLoading || isUpdateLoading}
                loading={isRejectLoading}
                onClick={handleRejectRequest}
                sx={{
                  ...actionButtonStyles,
                  backgroundColor: '#E2E8F0',
                  color: '#000000',
                }}
              >
                Reject
              </LoadingButton>
            ) : null}
            {isApprovable ? (
              <LoadingButton
                disableElevation
                variant="contained"
                color="primary"
                loading={isApproveLoading}
                disabled={isRejectLoading || isUpdateLoading}
                sx={{ ...actionButtonStyles }}
                onClick={handleApproveRequest}
              >
                Approve
              </LoadingButton>
            ) : null}
            {isUpdatable ? (
              <LoadingButton
                disableElevation
                variant="contained"
                color="primary"
                loading={isUpdateLoading}
                disabled={
                  isApproveLoading ||
                  isRejectLoading ||
                  !state.adminNote.trim().length
                }
                sx={{ ...actionButtonStyles }}
                onClick={handleUpdateRequest}
              >
                Update
              </LoadingButton>
            ) : null}
          </Stack>
        </DialogActions>
      ) : (
        <div style={{ minHeight: '30px' }} />
      )}
    </Dialog>
  );
};

export default ViewZoomRequestModal;
